import { inject, Injectable } from '@angular/core';
import { UserService } from '../user.service';
import { OrganizationService } from '../organization.service';
import { MemberService } from '../member.service';
import { PropertiesService } from '../property.service';
import { ServiceResultModel } from '../../models/service/service-result.model';
import { ServiceUtilities } from '../../utilities/service.utilities';
import { DashboardGlobalStats, DashboardOrganizationStats, DashboardRecentItem, InvoiceStatusCounts, MemberInvoiceSummary, MemberStatusCounts, PricingTierCounts } from '../../models/ui/dashboard.model';
import { PropertyChargeModel } from '../../models/data/property-charge.model';
import { DashboardConstants } from '../../constants/dashboard.constants';
import { ChargeStatus, MemberStatuses, PricingTiers, PropertyStatuses, UserStatuses } from '../../enums/data.enums';
import { ChargesService } from '../charge/charge.service';
import { PropertyChargeListSortOptions } from '../../enums/sort-options.enums';
import { UserModel } from '../../models/data/user.model';
import { MemberModel } from '../../models/data/member.model';
import { PropertyModel } from '../../models/data/property.model';
import { PropertiesFilter } from '../../filters/properties.filter';
import { UserListSortOptions, OrganizationListSortOptions, MemberListSortOptions, PropertyListSortOptions } from '../../enums/sort-options.enums';

@Injectable({
	providedIn: 'root'
})
export class DashboardService {

	private userService = inject(UserService);
	private organizationService = inject(OrganizationService);
	private memberService = inject(MemberService);
	private propertiesService = inject(PropertiesService);
	private chargesService = inject(ChargesService);

	//#region Global Stats

	public async getGlobalStats(): Promise<ServiceResultModel<DashboardGlobalStats>> {
		try {
			const [usersResponse, orgsResponse] = await Promise.all([
				// Only .total is read, so the sort is chosen to match the covering index rather than
				// the display: LastLoginDESC lands on the DEV-2621 Users index, the same one that
				// serves getRecentUsers. OrganizationNameASC likewise maps onto IX_Organizations_Name —
				// these orderBy values are load-bearing, not cosmetic, and must not be "simplified".
				this.userService.searchUsers(
					{ statuses: [UserStatuses.Active] },
					UserListSortOptions.LastLoginDESC, 0, 1
				),
				this.organizationService.searchOrganizations(
					{},
					OrganizationListSortOptions.OrganizationNameASC, 0, 1
				)
			]);

			return {
				data: {
					userCount: usersResponse.data?.total ?? 0,
					organizationCount: orgsResponse.data?.total ?? 0
				},
				success: true
			};
		} catch (error) {
			return ServiceUtilities.handleServiceException(error);
		}
	}

	//#endregion

	//#region Organization Stats

	public async getOrganizationStats(organizationId: number): Promise<ServiceResultModel<DashboardOrganizationStats>> {
		try {
			const [membersResponse, propertiesResponse] = await Promise.all([
				this.memberService.searchMembers(
					{ organizationIds: [organizationId], statuses: [MemberStatuses.Active] },
					MemberListSortOptions.NameASC, 0, 1
				),
				this.propertiesService.searchProperties(
					this.buildPropertiesFilter([organizationId]),
					PropertyListSortOptions.TitleASC, 0, 1
				)
			]);

			return {
				data: {
					memberCount: membersResponse.data?.total ?? 0,
					propertyCount: propertiesResponse.data?.total ?? 0
				},
				success: true
			};
		} catch (error) {
			return ServiceUtilities.handleServiceException(error);
		}
	}

	//#endregion

	//#region Recent Lists

	public async getRecentUsers(take: number = DashboardConstants.RECENT_LIST_TAKE): Promise<ServiceResultModel<DashboardRecentItem[]>> {
		try {
			const response = await this.userService.searchUsers(
				{ statuses: [UserStatuses.Active] },
				UserListSortOptions.LastLoginDESC, 0, take
			);

			const items: DashboardRecentItem[] = (response.data?.data ?? []).map((user: UserModel) => ({
				id: user.userId,
				primaryText: `${user.firstName} ${user.lastName}`,
				secondaryText: user.emailAddress,
				statusBadge: user.status,
				routerLink: `/users/profile/${user.userId}`,
				timestamp: user.lastLogin ? new Date(user.lastLogin) : undefined
			}));

			return { data: items, success: true };
		} catch (error) {
			return ServiceUtilities.handleServiceException(error);
		}
	}

	public async getRecentMembers(organizationId: number, take: number = DashboardConstants.RECENT_LIST_TAKE): Promise<ServiceResultModel<DashboardRecentItem[]>> {
		try {
			const response = await this.memberService.searchMembers(
				{ organizationIds: [organizationId], statuses: [MemberStatuses.Active] },
				MemberListSortOptions.LastLoginDESC, 0, take
			);

			const items: DashboardRecentItem[] = (response.data?.data ?? []).map((member: MemberModel) => ({
				id: member.memberId,
				primaryText: `${member.firstName} ${member.lastName}`,
				secondaryText: member.emailAddress,
				statusBadge: member.status,
				routerLink: `/members/profile/${member.memberId}`,
				timestamp: member.lastLogin ? new Date(member.lastLogin) : undefined
			}));

			return { data: items, success: true };
		} catch (error) {
			return ServiceUtilities.handleServiceException(error);
		}
	}

	public async getRecentProperties(organizationId: number, take: number = DashboardConstants.RECENT_LIST_TAKE): Promise<ServiceResultModel<DashboardRecentItem[]>> {
		try {
			const response = await this.propertiesService.searchProperties(
				this.buildPropertiesFilter([organizationId]),
				PropertyListSortOptions.TitleASC, 0, take
			);

			const items: DashboardRecentItem[] = (response.data?.data ?? []).map((property: PropertyModel) => ({
				id: property.propertyId,
				primaryText: property.title ?? 'Untitled Property',
				secondaryText: property.addresses?.[0]
					? `${property.addresses[0].primaryAddress}, ${property.addresses[0].city}`
					: undefined,
				statusBadge: property.status,
				routerLink: `/properties/profile/${property.propertyId}`
			}));

			return { data: items, success: true };
		} catch (error) {
			return ServiceUtilities.handleServiceException(error);
		}
	}

	public async getMemberProperties(organizationId: number, memberIds: number[], take: number = DashboardConstants.RECENT_LIST_TAKE): Promise<ServiceResultModel<DashboardRecentItem[]>> {
		try {
			const filter = this.buildPropertiesFilter([organizationId]);
			filter.memberIds = memberIds;

			const response = await this.propertiesService.searchProperties(
				filter, PropertyListSortOptions.TitleASC, 0, take
			);

			const items: DashboardRecentItem[] = (response.data?.data ?? []).map((property: PropertyModel) => ({
				id: property.propertyId,
				primaryText: property.title ?? 'Untitled Property',
				secondaryText: property.addresses?.[0]
					? `${property.addresses[0].primaryAddress}, ${property.addresses[0].city}`
					: undefined,
				statusBadge: property.status,
				routerLink: `/properties/profile/${property.propertyId}`
			}));

			return { data: items, success: true };
		} catch (error) {
			return ServiceUtilities.handleServiceException(error);
		}
	}

	//#endregion

	//#region Member Invoice Data

	public async getMemberInvoiceSummary(organizationId: number, memberId: number): Promise<ServiceResultModel<MemberInvoiceSummary>> {
		try {
			const baseFilter = { memberIds: [memberId], organizationIds: [organizationId] };

			const [open, late, paid] = await Promise.all([
				this.chargesService.searchPropertyCharges(
					{ ...baseFilter, statuses: [ChargeStatus.Open] },
					PropertyChargeListSortOptions.PropertyChargeIdAsc, 0, 1
				),
				this.chargesService.searchPropertyCharges(
					{ ...baseFilter, statuses: [ChargeStatus.Late] },
					PropertyChargeListSortOptions.PropertyChargeIdAsc, 0, 1
				),
				this.chargesService.searchPropertyCharges(
					{ ...baseFilter, statuses: [ChargeStatus.Paid, ChargeStatus.PaidLate] },
					PropertyChargeListSortOptions.PropertyChargeIdAsc, 0, 1
				)
			]);

			return {
				data: {
					openCount: open.data?.total ?? 0,
					lateCount: late.data?.total ?? 0,
					paidCount: paid.data?.total ?? 0
				},
				success: true
			};
		} catch (error) {
			return ServiceUtilities.handleServiceException(error);
		}
	}

	public async getMemberOutstandingInvoices(organizationId: number, memberId: number, take: number = DashboardConstants.RECENT_LIST_TAKE): Promise<ServiceResultModel<PropertyChargeModel[]>> {
		try {
			const response = await this.chargesService.searchPropertyCharges(
				{
					memberIds: [memberId],
					organizationIds: [organizationId],
					statuses: [ChargeStatus.Open, ChargeStatus.Late, ChargeStatus.Locked]
				},
				PropertyChargeListSortOptions.DueAsc, 0, take
			);

			return { data: response.data?.data ?? [], success: true };
		} catch (error) {
			return ServiceUtilities.handleServiceException(error);
		}
	}

	//#endregion

	//#region Chart Data

	public async getInvoiceStatusCounts(organizationId: number): Promise<ServiceResultModel<InvoiceStatusCounts>> {
		try {
			const [open, late, paid, voided] = await Promise.all([
				this.chargesService.searchPropertyCharges({ organizationIds: [organizationId], statuses: [ChargeStatus.Open] }, PropertyChargeListSortOptions.PropertyChargeIdAsc, 0, 1),
				this.chargesService.searchPropertyCharges({ organizationIds: [organizationId], statuses: [ChargeStatus.Late] }, PropertyChargeListSortOptions.PropertyChargeIdAsc, 0, 1),
				this.chargesService.searchPropertyCharges({ organizationIds: [organizationId], statuses: [ChargeStatus.Paid, ChargeStatus.PaidLate] }, PropertyChargeListSortOptions.PropertyChargeIdAsc, 0, 1),
				this.chargesService.searchPropertyCharges({ organizationIds: [organizationId], statuses: [ChargeStatus.Voided] }, PropertyChargeListSortOptions.PropertyChargeIdAsc, 0, 1)
			]);

			return {
				data: {
					open: open.data?.total ?? 0,
					late: late.data?.total ?? 0,
					paid: paid.data?.total ?? 0,
					voided: voided.data?.total ?? 0
				},
				success: true
			};
		} catch (error) {
			return ServiceUtilities.handleServiceException(error);
		}
	}

	public async getMemberStatusCounts(organizationId: number, includeDeleted: boolean): Promise<ServiceResultModel<MemberStatusCounts>> {
		try {
			// All Member statuses per DEV-2505 except Suspended (legacy DEV-only status
			// that Prod can no longer produce — intentionally excluded from the chart).
			// Deleted is only queried when the acting user has members.read_deleted.
			const queries = [
				this.memberService.searchMembers({ organizationIds: [organizationId], statuses: [MemberStatuses.Created] }, MemberListSortOptions.NameASC, 0, 1),
				this.memberService.searchMembers({ organizationIds: [organizationId], statuses: [MemberStatuses.Invited] }, MemberListSortOptions.NameASC, 0, 1),
				this.memberService.searchMembers({ organizationIds: [organizationId], statuses: [MemberStatuses.Expired] }, MemberListSortOptions.NameASC, 0, 1),
				this.memberService.searchMembers({ organizationIds: [organizationId], statuses: [MemberStatuses.Active] }, MemberListSortOptions.NameASC, 0, 1),
				this.memberService.searchMembers({ organizationIds: [organizationId], statuses: [MemberStatuses.Deactivated] }, MemberListSortOptions.NameASC, 0, 1)
			];

			if (includeDeleted) {
				queries.push(this.memberService.searchMembers({ organizationIds: [organizationId], statuses: [MemberStatuses.Deleted], includeDeletedMembers: true }, MemberListSortOptions.NameASC, 0, 1));
			}

			const [created, invited, expired, active, deactivated, deleted] = await Promise.all(queries);

			const data: MemberStatusCounts = {
				created: created.data?.total ?? 0,
				invited: invited.data?.total ?? 0,
				expired: expired.data?.total ?? 0,
				active: active.data?.total ?? 0,
				deactivated: deactivated.data?.total ?? 0
			};

			if (includeDeleted) {
				data.deleted = deleted?.data?.total ?? 0;
			}

			return { data, success: true };
		} catch (error) {
			return ServiceUtilities.handleServiceException(error);
		}
	}

	public async getOrganizationsByPricingTier(): Promise<ServiceResultModel<PricingTierCounts>> {
		try {
			const [standard, premium, whiteGlove, custom, trial] = await Promise.all([
				this.organizationService.searchOrganizations({ pricingTiers: [PricingTiers.Standard] }, OrganizationListSortOptions.OrganizationNameASC, 0, 1),
				this.organizationService.searchOrganizations({ pricingTiers: [PricingTiers.Premium] }, OrganizationListSortOptions.OrganizationNameASC, 0, 1),
				this.organizationService.searchOrganizations({ pricingTiers: [PricingTiers.WhiteGlove] }, OrganizationListSortOptions.OrganizationNameASC, 0, 1),
				this.organizationService.searchOrganizations({ pricingTiers: [PricingTiers.Custom] }, OrganizationListSortOptions.OrganizationNameASC, 0, 1),
				this.organizationService.searchOrganizations({ pricingTiers: [PricingTiers.Trial] }, OrganizationListSortOptions.OrganizationNameASC, 0, 1)
			]);

			return {
				data: {
					standard: standard.data?.total ?? 0,
					premium: premium.data?.total ?? 0,
					whiteGlove: whiteGlove.data?.total ?? 0,
					custom: custom.data?.total ?? 0,
					trial: trial.data?.total ?? 0
				},
				success: true
			};
		} catch (error) {
			return ServiceUtilities.handleServiceException(error);
		}
	}

	//#endregion

	//#region Private Helpers

	private buildPropertiesFilter(organizationIds: number[]): PropertiesFilter {
		return {
			organizationIds: organizationIds,
			statuses: [PropertyStatuses.Active],
			propertyIds: [],
			titles: [],
			lotNumbers: [],
			addressTypes: [],
			primaryAddresses: [],
			secondaryAddresses: [],
			cities: [],
			stateProvinces: [],
			primaryZips: [],
			countries: []
		};
	}

	//#endregion
}
